import {
  Box2,
  Camera,
  Frustum,
  Group,
  Matrix4,
  PlaneHelper,
  Vector2,
} from "three";
import axios, { AxiosResponse, CancelTokenSource } from "axios";
import { config } from "../../config/config";
import { MeasurementType } from "../measurement/types";
import { IVolumeCalculationMeasurement } from "../measurement/VolumeCalculationMeasurement";
import { FrustumUtil } from "../utils/math-utils";
import { Viewer } from "../viewer";
import { PointCloudFactory } from "./PointCloud";
import { IPointCloudTile } from "./PointCloudTile";
import { Queue } from "queue-typescript";
import { PointsMaterialHandler } from "./PointCloudMaterialHandler";
import { ORIGINAL, ITextScale, SCENE_TYPE } from "../config/ViewerConfig";
import { resolve } from "path";

interface requestObject {
  objectName: string;
  floor: string;
  type: string;
  projectId: string;
  config: any;
}

//Root tile will always be loaded
export class PointCloudStreamHandler {
  private _viewer: Viewer;
  private _rgbGroup: Group;
  // private _pickGroup: Group;
  private _rootTile: IPointCloudTile;
  private _camerafrustum: Frustum;
  private _rootURL: string | { baseKey: string; bucket: string };
  private _debugGroup: Group;
  private _pointCloudFactory: PointCloudFactory;
  private _type: string;
  private _projectId: string;
  private _timestamp: string;
  // private _isHeatMapVisible: boolean;
  private _tSplitThresHoldNow: number;
  private _isDebug: boolean;
  private _workers: any[];
  private _tileMapPickIndexToUrl: Map<number, string>; // tile url to all available tileMap
  private _usableIndex: Queue<number>;
  private _pointCloudMaterialHandler: PointsMaterialHandler;

  public getRgbGroup() {
    return this._rgbGroup;
  }

  public getDebugRenderable() {
    return this._debugGroup;
  }

  public getTileMapPickIndexToUrl() {
    return this._tileMapPickIndexToUrl;
  }

  public setTileMapPickIndexToUrlForOriginal() {
    this._tileMapPickIndexToUrl.set(0, ORIGINAL);
  }

  numberToUint8Array = (num: number): Uint8Array => {
    if (num < 0 || num > 16777216 || !Number.isInteger(num)) {
      throw new Error(
        "Input must be a non-negative integer within the range of 0 to 16777216."
      );
    }

    const byteArray = new Uint8Array(3);
    byteArray[0] = num & 0xff;
    byteArray[1] = (num >> 8) & 0xff;
    byteArray[2] = (num >> 16) & 0xff;

    return byteArray;
  };

  public makePickIndex = (num: number): Uint8Array => {
    const res = new Uint8Array(num * 3);

    for (let i = 0; i < num; i++) {
      const startIndex = i * 3;
      const uint8Array = this.numberToUint8Array(i);
      res.set(uint8Array, startIndex);
    }

    return res;
  };

  constructor(
    viewer: Viewer,
    pointCloudMaterialHandler: PointsMaterialHandler,
    rootTile: IPointCloudTile,
    rootUrl: string | { baseKey: string; bucket: string },
    pointCloudFactory: PointCloudFactory,
    type: string,
    projectId: string,
    timestamp: string
  ) {
    this._viewer = viewer;
    this._pointCloudMaterialHandler = pointCloudMaterialHandler;
    this._rgbGroup = new Group();
    // this._pickGroup = new Group();
    this._camerafrustum = new Frustum();
    this._rootTile = rootTile;
    this._rootURL = rootUrl;
    this._pointCloudFactory = pointCloudFactory;
    this._type = type;
    this._projectId = projectId;
    this._timestamp = timestamp;
    // this._isHeatMapVisible = true;

    this._debugGroup = new Group();
    this._tSplitThresHoldNow = 1;
    this._workers = [];

    this._tileMapPickIndexToUrl = new Map();

    this._usableIndex = new Queue();
  }

  /**
   * カメラ範囲内のタイル配列返却
   * @param frustum - カメラ範囲
   * @returns {Array} - カメラ範囲内のタイル配列
   */
  private getTilesToLoad(
    frustum: Frustum
  ): [
    { [level: number]: Set<IPointCloudTile> },
    { [level: number]: Set<IPointCloudTile> },
    { [level: number]: Set<IPointCloudTile> },
    number
  ] {
    /**
     * カメラ範囲内タイルの存在判定
     * @param _frustum - カメラ範囲
     * @param _tile - 子要素を含むタイル群
     * @returns
     */
    const _getTilesToLoad = (
      _frustum: Frustum,
      _tile: IPointCloudTile
    ): IPointCloudTile[] => {
      // 衝突判定
      const intersection = FrustumUtil.intersectsBox(
        _frustum,
        // _tile.tileBBox,
        _tile.boundingBox,
        this._tSplitThresHoldNow
      );

      // 0 タイルなし
      if (intersection === FrustumUtil.TileIntersectsFrustum.noIntersection) {
        return [];
      }
      // 1 一部画面内
      else if (
        intersection === FrustumUtil.TileIntersectsFrustum.justIntersects
      ) {
        const result: any[] = [];
        _tile.frustum = FrustumUtil.TileIntersectsFrustum.justIntersects;
        result.push(_tile);
        if (_tile.children.length > 0) {
          for (const child of _tile.children) {
            result.push(..._getTilesToLoad(_frustum, child));
          }
        }
        return result;
        // 2 完全に画面内
      } else if (
        intersection === FrustumUtil.TileIntersectsFrustum.iscompletelyInside
      ) {
        const result: any[] = [];
        _tile.frustum = FrustumUtil.TileIntersectsFrustum.iscompletelyInside;
        result.push(_tile);
        if (_tile.children.length > 0) {
          for (const child of _tile.children) {
            result.push(..._getTilesToLoad(_frustum, child));
          }
        }
        return result;
      }
      return [];
    };

    let maxLevel = 0;

    // 子ありタイル群
    let commonTiles: IPointCloudTile[] = [];
    let commonLayers: { [level: number]: Set<IPointCloudTile> } = {};
    let accumulatedCommonLayers: { [level: number]: Set<IPointCloudTile> } = {};

    // 子なしタイル群
    let uniqueTiles: IPointCloudTile[] = [];
    let uniqueLayers: { [level: number]: Set<IPointCloudTile> } = {};

    // 全てのタイルから画面内タイルを取得
    for (const c of this._rootTile.children) {
      const tiles = _getTilesToLoad(frustum, c);
      for (const tile of tiles) {
        const level = tile.url.split("/").length;

        if (tile.children.length === 0) {
          uniqueTiles.push(tile);
          uniqueLayers[level]
            ? uniqueLayers[level].add(tile)
            : (uniqueLayers[level] = new Set<IPointCloudTile>().add(tile));
        } else {
          commonTiles.push(tile);
          commonLayers[level]
            ? commonLayers[level].add(tile)
            : (commonLayers[level] = new Set<IPointCloudTile>().add(tile));
        }

        maxLevel = Math.max(maxLevel, level);
      }
    }

    // uniqueLayersの作成
    for (const tile of uniqueTiles) {
      const _ = tile.url.split("/").length;
      for (let level = _; level <= maxLevel; ++level) {
        uniqueLayers[level]
          ? uniqueLayers[level].add(tile)
          : (uniqueLayers[level] = new Set<IPointCloudTile>().add(tile));
      }
    }

    // accumulatedCommonLayersの作成
    for (const tile of commonTiles) {
      const _ = tile.url.split("/").length;
      for (let level = _; level <= maxLevel; ++level) {
        accumulatedCommonLayers[level]
          ? accumulatedCommonLayers[level].add(tile)
          : (accumulatedCommonLayers[level] = new Set<IPointCloudTile>().add(
              tile
            ));
      }
    }

    // console.log("==================================================");
    // console.log("commonLayers", commonLayers);
    // console.log("uniqueLayers", uniqueLayers);
    // console.log("accumulatedCommonLayers", accumulatedCommonLayers);

    return [commonLayers, accumulatedCommonLayers, uniqueLayers, maxLevel];
  }

  public getMarkedToLoadTiles(): Map<string, IPointCloudTile[]> {
    const result: Map<string, IPointCloudTile[]> = new Map();
    const _parseTiles = (tile: IPointCloudTile) => {
      const t = result.get(tile.loadingStatus);
      if (t) {
        t.push(tile);
      } else {
        result.set(tile.loadingStatus, [tile]);
      }

      for (const child of tile.children) {
        _parseTiles(child);
      }
    };
    for (const c of this._rootTile.children) {
      _parseTiles(c);
    }
    return result;
  }

  public getRenderedTiles(): IPointCloudTile[] {
    const result: IPointCloudTile[] = [];
    const _parseTiles = (tile: IPointCloudTile) => {
      if (tile.pointcloud) {
        result.push(tile);
      }
      for (const child of tile.children) {
        _parseTiles(child);
      }
    };
    for (const c of this._rootTile.children) {
      _parseTiles(c);
    }
    return result;
  }

  public async fetchAndRenderTiles(
    tilesToFetch: IPointCloudTile[],
    tileFloor: string[],
    tileTypes: string[],
    tileProjectIds: string[],
    abortController: AbortController,
    timestamp: string
  ): Promise<IPointCloudTile[]> {
    const volMeasurements = this._viewer.getMeasurementByType(
      MeasurementType.VOLUME_CALCULATION
    );

    const CancelTokenSource = axios.CancelToken.source();

    const binsRequests: requestObject[][] = [];
    for (let i = 0; i < tilesToFetch.length; i++) {
      binsRequests.push([
        {
          objectName: "points.bin",
          floor: tileFloor[i],
          type: tileTypes[i],
          projectId: tileProjectIds[i],
          config: {
            // withCredentials: true,
            responseType: "arraybuffer",
            signal: abortController.signal,
          },
        },
        {
          objectName: "colors.bin",
          floor: tileFloor[i],
          type: tileTypes[i],
          projectId: tileProjectIds[i],
          config: {
            // withCredentials: true,
            responseType: "arraybuffer",
            signal: abortController.signal,
          },
        },
      ]);
    }

    try {
      const makeFetchUrl = (binRequest: any): [string, boolean] => {
        let rootFlag = false;

        let folderName: string;
        if (binRequest.type === "simple") {
          folderName = "point_cloud_split_upload";
        } else if (binRequest.type === "sfm") {
          folderName = "point_cloud_split_SfM";
        } else if (binRequest.type === "helmert") {
          folderName = "point_cloud_split_Helmert";
        }

        let name: string;
        if (binRequest.floor === "first") {
          name = binRequest.objectName;
          rootFlag = true;
        } else {
          const floor = binRequest.floor.split("-").join("/"); // "-"を"/"で置換
          name = floor + "/" + binRequest.objectName;
        }

        let hostName = config.serverHost.includes("localhost")
          ? "http://localhost:9090/quick3d"
          : config.serverHost;
        return [
          `${hostName}/project_${binRequest.projectId}/${folderName}/${name}?timestamp=${timestamp}`,
          rootFlag,
        ];
      };

      const renderTile = (index: number, binsData: any) => {
        let vertices = new Float32Array(binsData.points);
        let colors = new Float32Array(binsData.colors);
        let indices = this.makePickIndex(vertices.length / 3);

        let tileIndex: number;

        if (this._usableIndex.length !== 0) {
          tileIndex = this._usableIndex.dequeue();
        } else {
          tileIndex = this._tileMapPickIndexToUrl.size;
        }

        this._tileMapPickIndexToUrl.set(tileIndex, tilesToFetch[index].url);

        const pointCloud = this._pointCloudFactory.createPointCloud(
          {
            vertices: vertices,
            color: colors,
            indices: indices,
          },
          this._pointCloudMaterialHandler.pointMaterial,
          tilesToFetch[index],
          { orthographic: 0.09, perspective: 0.18 } as ITextScale,
          this._pointCloudMaterialHandler.createPickMaterial(tileIndex)
        );

        for (const volMeasurement of volMeasurements as IVolumeCalculationMeasurement[]) {
          volMeasurement.heatMapVisibility &&
            pointCloud.applyHeightBasedColor(
              volMeasurement.pointColorRangeInfo,
              volMeasurement.polygonMesh,
              new Box2().setFromPoints(
                volMeasurement.polygonMesh.vertices as unknown as Vector2[]
              )
            );
        }

        const mesh = pointCloud.getMesh();
        this._rgbGroup.add(mesh);

        tilesToFetch[index].pointcloud = pointCloud;
        tilesToFetch[index].loadingStatus = "loaded";
        if (tilesToFetch.length !== binsRequests.length) {
          console.error("tilesToFetch and response doesn't match");
        }

        if (this._isDebug) {
          const debug = pointCloud.getDebug();
          this._debugGroup.add(debug);
        }

        this._viewer.updateRenderer();
      };

      const MAX_CONCURRENCY = 3;

      // workerがある場合は終了
      while (true) {
        const worker = this._workers.pop();
        if (worker === undefined) {
          break;
        } else {
          worker.terminate();
        }
      }

      let i = 0;
      let fetchAndRenderTilePromises: Promise<any>[] = [];

      // リクエストが残っている or promiseが残っている
      while (i < binsRequests.length || 0 < fetchAndRenderTilePromises.length) {
        // WebWorkerを使ってデータ取得 => render
        const fetchAndRenderTile = async (
          binsUrl: { points: string; colors: string },
          index: number,
          rootFlag: boolean
        ) => {
          return new Promise<any>((resolve, reject) => {
            const worker = new Worker(
              `${process.env.REACT_APP_WEB_SERVER_URL}/PointCloudLoadWorker/PointCloudLoadWorker.js`
            );

            // ベースとなるrootタイルはworker終了の管理をする必要がない
            if (!rootFlag) {
              this._workers.push(worker);
            }

            worker.postMessage(binsUrl);

            worker.onmessage = function (event: any) {
              if (event.data === undefined) {
                reject();
              } else {
                renderTile(index, event.data);
                resolve(event.data);
              }

              worker.terminate();
            };
          });
        };

        // 次に並列処理するpromiseの準備: リクエストが残っている or fetchAndRenderTilePromiseを最大並列数を超えない
        while (
          i < binsRequests.length &&
          fetchAndRenderTilePromises.length < MAX_CONCURRENCY
        ) {
          const binsRequest = binsRequests[i];

          let binsUrl: { points: string; colors: string } = {
            points: "",
            colors: "",
          };
          let pointsUrl: [string, boolean] = makeFetchUrl(binsRequest[0]);
          binsUrl["points"] = pointsUrl[0];

          let colorsUrl: [string, boolean] = makeFetchUrl(binsRequest[1]);
          binsUrl["colors"] = colorsUrl[0];

          let rootFlag = false;
          if (pointsUrl[1] === true && colorsUrl[1] === true) {
            rootFlag = true;
          }

          fetchAndRenderTilePromises.push(
            fetchAndRenderTile(binsUrl, i, rootFlag)
          );
          ++i;
        }

        // 配列の中の全てのpromiseを走らせ，最初に完了したindexを取得
        const completedIndex = await Promise.race(
          fetchAndRenderTilePromises.map((p, index) => p.then(() => index))
        );

        // 完了したpromiseを配列から削除
        fetchAndRenderTilePromises.splice(completedIndex, 1);
      }

      return tilesToFetch;
    } catch (e) {
      if (axios.isCancel(e)) {
        console.log("auto cancelation by axios");
      } else {
        // 読み込めない場合タイル分割閾値を半分にし再読み込み
        if (e instanceof Error) {
          CancelTokenSource.cancel();
          this._viewer.updateRenderer();
        } else {
          throw e;
        }
      }
    }
  }

  public async execFetchAndRenderTiles(
    tiles: IPointCloudTile[],
    abortController: AbortController
  ) {
    return new Promise<IPointCloudTile[]>((resolve, reject) => {
      const tilesToFetch = [];
      const tileUrls = [];
      const tileTypes = [];
      const tileProjectIds = [];

      for (const tile of tiles) {
        if (tile.loadingStatus !== "loaded") {
          tilesToFetch.push(tile);
          const url = tile.url.split("/").join("-"); // "/"を"-"で置換
          tileUrls.push(url);
          tileTypes.push(this._type);
          tileProjectIds.push(this._projectId);
          tile.loadingStatus = "loading";
        }
      }

      if (tilesToFetch) {
        this.fetchAndRenderTiles(
          tilesToFetch,
          tileUrls,
          tileTypes,
          tileProjectIds,
          abortController,
          this._timestamp
        )
          .then((tilesFetched) => {
            resolve(tilesFetched);
          })
          .catch((e) => {
            reject(e);
          });
      } else {
        resolve([]);
      }
    });
  }

  public async deleteTiles(tilesToDelete: IPointCloudTile[]) {
    return new Promise<IPointCloudTile[]>((resolve, reject) => {
      for (const tileToDelete of tilesToDelete) {
        if (tileToDelete.pointcloud) {
          // console.log("dispose");
          tileToDelete.pointcloud.dispose();
          tileToDelete.pointcloud = undefined;
          tileToDelete.loadingStatus = "removed";

          this._tileMapPickIndexToUrl.forEach((value, key) => {
            if (value === tileToDelete.url) {
              this._tileMapPickIndexToUrl.delete(key);
              this._usableIndex.enqueue(key);
            }
          });
        }
      }
      resolve([]);
    });
  }

  /**
   *
   * 初期化・カメラ移動完了時に以下を実行
   *     カメラ範囲内のタイルを取得
   *     ロードされていないタイルのみをLoadする
   * @param camera
   * @param abortController
   * @returns
   */
  public async onCameraChange(
    camera: Camera,
    abortController: AbortController
  ) {
    this._camerafrustum.setFromProjectionMatrix(
      new Matrix4().multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse
      )
    );

    // 透視投影の点の大きさ調整用_マテリアルにrootタイルのBBOX対角線長とカメラの座標をセット
    this._pointCloudMaterialHandler?.setCameraInfo(
      this._viewer.getRootLength(),
      this._viewer.getCamera().position
    );

    const fetchAndRenderLayer = async (tiles: IPointCloudTile[]) => {
      if (tiles.length > 0) {
        try {
          await this.execFetchAndRenderTiles(tiles, abortController);
        } catch (error) {
          // console.error("Error in fetching and rendering tiles", error);
        }
      } else {
        // console.log("No tiles to fetch and render");
      }
    };

    if (this._rootTile && this._rootTile.isVisible) {
      const MAX_NUM_OF_TILES = 70;
      const MAX_TARGET_NUM_OF_TILES = 35;

      const [commonLayers, accumulatedCommonLayers, uniqueLayers, maxLevel] =
        this.getTilesToLoad(this._camerafrustum);

      // 削除するタイルの選定
      let offScreenTiles: IPointCloudTile[] = [];
      let onScreenTiles: IPointCloudTile[] = [];
      const loadedTiles = this.getRenderedTiles();

      // console.log("loadedTiles", loadedTiles);

      for (const tile of loadedTiles) {
        if (
          !(
            accumulatedCommonLayers[maxLevel]?.has(tile) ||
            uniqueLayers[maxLevel]?.has(tile)
          )
        ) {
          offScreenTiles.push(tile);
        } else {
          onScreenTiles.push(tile);
        }
      }

      let targetLevel = maxLevel;

      // targetLevelの決定
      for (let level = 1; level <= maxLevel; ++level) {
        // 描画されうる最大のタイル数: 画面内タイルとこれから描画されるタイルの和集合
        const potentialMaxTiles = new Set([
          ...(uniqueLayers[level] ?? []),
          ...(accumulatedCommonLayers[level] ?? []),
          ...(onScreenTiles ?? []),
        ]);

        const targetTiles = new Set([
          ...(uniqueLayers[level] ?? []),
          ...(accumulatedCommonLayers[level] ?? []),
        ]);

        if (MAX_NUM_OF_TILES < potentialMaxTiles.size) {
          // console.log("potential limit");
          // console.log("potentialMaxTiles", potentialMaxTiles);
          // console.log("targetTiles", targetTiles);
          targetLevel = level - 1;
          break;
        }

        if (MAX_TARGET_NUM_OF_TILES < targetTiles.size) {
          // console.log("target limit");
          // console.log("potentialMaxTiles", potentialMaxTiles);
          // console.log("targetTiles", targetTiles);
          targetLevel = level - 1;
          break;
        }
      }

      // console.log("MAX_NUM_OF_TILES", MAX_NUM_OF_TILES);
      // console.log("targetLevel", targetLevel);
      // console.log("offScreenTiles", offScreenTiles);

      // 想定表示タイル: debug用
      // const expectedTiles = new Set([
      //   ...(uniqueLayers[targetLevel] ?? []),
      //   ...(accumulatedCommonLayers[targetLevel] ?? []),
      //   ...(onScreenTiles ?? []),
      // ]);

      // console.log("expectedTiles", expectedTiles);

      // offScreenTilesの削除
      await this.deleteTiles(offScreenTiles);

      for (let level = 1; level <= targetLevel; ++level) {
        // console.log("level", level);

        await fetchAndRenderLayer([
          ...(commonLayers[level] ?? []),
          ...(uniqueLayers[level] ?? []),
        ]);

        // 一個上の層の削除
        // await this.deleteTiles([...(commonLayers[level - 1] ?? [])]);
      }

      // targetより深いタイルを削除
      let tooDeepTiles: IPointCloudTile[] = [];
      for (const tile of loadedTiles) {
        if (
          !(
            accumulatedCommonLayers[targetLevel]?.has(tile) ||
            uniqueLayers[targetLevel]?.has(tile)
          )
        ) {
          tooDeepTiles.push(tile);
        }
      }

      // console.log("tooDeepTiles", tooDeepTiles);

      await this.deleteTiles(tooDeepTiles);

      // console.log("end");
    }

    return;
  }
  public setHeatMapVisibility(visibility: boolean): void {
    // this._isHeatMapVisible = visibility;
    this._rootTile?.setHeatMapVisibility(visibility);
  }

  /**
   * データが大きい場合、タイルを分割（分割開始位置を遅らせる）計算
   * スライダー初期位置は中央
   * スライダーは0～10、閾値を相対的にずらす（例:-6～+4、-1～9など）
   * @param tlSplitNum - スライダーの値
   * @returns - スライダ値 - (スライダ範囲/2) + (-3 * 幅分割比率);
   */
  public setTileSplit(tlSplitNum: number = 0): void {
    this._tSplitThresHoldNow = tlSplitNum;
  }

  public getTileSplit() {
    return this._tSplitThresHoldNow;
  }

  public toggleIsDebug() {
    this._isDebug = !this._isDebug;
  }
}
